/**
 * The founding-customer offer on the site and in the app (code ZAKLADATEL).
 * The rules and the copy live in supabase/functions/_shared/founding-offer.ts;
 * this mirror holds the same constants (checked equal by
 * src/lib/billing/__tests__/foundingOffer.test.ts) and asks the public
 * `founding-offer` function whether to show it.
 *
 * Prerender safety: nothing here runs while rendering. The offer is not in the
 * static markup; it appears after the page loads, only while Stripe says
 * places are left, so a sold-out offer is never shown, not even for a moment.
 * Plain fetch, no Supabase client, so the landing stays free of it.
 */

export const FOUNDING_OFFER = {
  code: "ZAKLADATEL",
  percentOff: 50,
  months: 12,
  maxRedemptions: 20,
} as const;

export interface FoundingOffer {
  available: boolean;
  remaining: number | null;
}

/** The one sentence, word for word as the owner approved it. */
export function foundingOfferSentence(language: "cs" | "en"): string {
  const { code, percentOff, months, maxRedemptions } = FOUNDING_OFFER;
  return language === "en"
    ? `Founding customers: −${percentOff} % for ${months} months for the first ${maxRedemptions} companies, code ${code}.`
    : `Zakládající zákazníci: −${percentOff} % na ${months} měsíců pro prvních ${maxRedemptions} firem, kód ${code}.`;
}

export function remainingSentence(language: "cs" | "en", remaining: number): string {
  if (language === "en") return `${remaining} ${remaining === 1 ? "place" : "places"} left.`;
  if (remaining === 1) return "Zbývá 1 místo.";
  if (remaining <= 4) return `Zbývají ${remaining} místa.`;
  return `Zbývá ${remaining} míst.`;
}

/** Reads the function's answer defensively: anything unexpected means "don't show". */
export function parseFoundingOffer(body: unknown): FoundingOffer {
  const b = body as { available?: unknown; remaining?: unknown; code?: unknown } | null;
  const remaining = typeof b?.remaining === "number" ? b.remaining : null;
  const available =
    b?.available === true &&
    b?.code === FOUNDING_OFFER.code &&
    remaining !== null &&
    Number.isInteger(remaining) &&
    remaining > 0 &&
    remaining <= FOUNDING_OFFER.maxRedemptions;
  return { available, remaining: available ? remaining : null };
}

const HIDDEN: FoundingOffer = { available: false, remaining: null };

/** Asks the public function; any failure hides the offer. */
/**
 * Where to ask. Production and previews: `/api/founding-offer` on the page's
 * own origin, a Vercel Edge Function (api/founding-offer.ts) that the CDN
 * caches (5 min), so visitors almost never reach Supabase. The dev server has
 * no rewrite, so it calls the function URL (the UI harness answers it).
 */
export function foundingOfferUrl(
  dev: boolean = import.meta.env.DEV,
  supabaseUrl: string | undefined = import.meta.env.VITE_SUPABASE_URL
): string {
  if (!dev) return "/api/founding-offer";
  const base = (supabaseUrl || "https://placeholder.supabase.co").replace(/\/$/, "");
  return `${base}/functions/v1/founding-offer`;
}

/**
 * Asks the function; any failure hides the offer. No headers at all: the
 * function needs no key (verify_jwt = false) and a bare GET stays cacheable.
 */
export async function fetchFoundingOffer(
  url: string = foundingOfferUrl(),
  fetchImpl: typeof fetch = (input, init) => fetch(input, init)
): Promise<FoundingOffer> {
  try {
    const res = await fetchImpl(url, { method: "GET", credentials: "omit" });
    if (!res.ok) return HIDDEN;
    return parseFoundingOffer(await res.json());
  } catch {
    return HIDDEN;
  }
}
